import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, BarChart3, BookOpen } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

// --- Chat Conversation Data ---

const MiniBarChart = () => (
  <div className="flex items-end gap-1.5 h-12 mt-2">
    {[
      { h: 72, label: 'Q1' },
      { h: 64, label: 'Q2' },
      { h: 55, label: 'Q3' },
      { h: 48, label: 'Q4' },
    ].map((bar, i) => (
      <div key={i} className="flex flex-col items-center gap-1 flex-1">
        <div
          className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-sm"
          style={{ height: `${bar.h}%` }}
        />
        <span className="text-[9px] text-gray-400">{bar.label}</span>
      </div>
    ))}
  </div>
);

const ComplianceChecklist = () => (
  <div className="mt-2 space-y-1.5">
    {[
      { label: 'GRI 302-1: Energy consumption', done: true },
      { label: 'GRI 302-3: Energy intensity', done: true },
      { label: 'GRI 302-4: Reduction of energy', done: false },
    ].map((item, i) => (
      <div key={i} className="flex items-center gap-2 text-xs">
        <span
          className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
            item.done ? 'bg-green-100' : 'bg-amber-100'
          }`}
        >
          {item.done ? (
            <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-2.5 h-2.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4m0 4h.01" />
            </svg>
          )}
        </span>
        <span className={item.done ? 'text-gray-600 dark:text-slate-400' : 'text-amber-700 dark:text-amber-400 font-medium'}>
          {item.label}
        </span>
      </div>
    ))}
  </div>
);

const ComparisonBars = () => (
  <div className="mt-2 space-y-2">
    <div>
      <div className="flex justify-between text-[10px] mb-0.5">
        <span className="text-gray-500 dark:text-slate-400">Your company</span>
        <span className="font-semibold text-green-600">85%</span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-slate-600 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full" style={{ width: '85%' }} />
      </div>
    </div>
    <div>
      <div className="flex justify-between text-[10px] mb-0.5">
        <span className="text-gray-500 dark:text-slate-400">Industry average</span>
        <span className="font-medium text-gray-600 dark:text-slate-400">100%</span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-slate-600 rounded-full overflow-hidden">
        <div className="h-full bg-gray-300 dark:bg-slate-500 rounded-full" style={{ width: '100%' }} />
      </div>
    </div>
  </div>
);

const conversations = [
  {
    question: "What's our Scope 2 emissions trend?",
    answer: 'Your Scope 2 emissions decreased 12% YoY, driven by the renewable energy transition in Q3.',
    visual: <MiniBarChart />,
  },
  {
    question: 'Generate a GRI compliance summary',
    answer: "Based on your data, here's your GRI 302 compliance status across energy indicators:",
    visual: <ComplianceChecklist />,
  },
  {
    question: 'Compare our carbon footprint to industry benchmarks',
    answer: 'Your carbon intensity is 15% below sector average. You outperform 78% of peers.',
    visual: <ComparisonBars />,
  },
];

// --- Typing Indicator ---

const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-3 py-2">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 bg-gray-400 rounded-full"
        animate={{ y: [0, -4, 0] }}
        transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: i * 0.15,
          ease: 'easeInOut',
        }}
      />
    ))}
  </div>
);

// --- Chat Window ---

const ChatWindow = () => {
  const [activeConvo, setActiveConvo] = useState(0);
  const [phase, setPhase] = useState('question'); // 'question' | 'typing' | 'answer'
  const timerRef = useRef(null);

  useEffect(() => {
    const cycle = () => {
      // Show question
      setPhase('question');

      timerRef.current = setTimeout(() => {
        // Show typing indicator
        setPhase('typing');

        timerRef.current = setTimeout(() => {
          // Show answer
          setPhase('answer');

          timerRef.current = setTimeout(() => {
            // Move to next conversation
            setActiveConvo((prev) => (prev + 1) % conversations.length);
          }, 5000);
        }, 1200);
      }, 1500);
    };

    cycle();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeConvo]);

  const convo = conversations[activeConvo];

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      custom={1}
      className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-slate-700/50 overflow-hidden"
    >
      {/* Chat Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60">
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-2.47 2.47a2.25 2.25 0 01-1.591.659H9.061a2.25 2.25 0 01-1.591-.659L5 14.5m14 0V17a2.25 2.25 0 01-2.25 2.25H7.25A2.25 2.25 0 015 17v-2.5"
              />
            </svg>
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white dark:border-slate-800 rounded-full" />
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-800 dark:text-slate-200">ESG AI Assistant</div>
          <div className="text-xs text-green-500 font-medium">Online</div>
        </div>

        {/* Conversation dots */}
        <div className="ml-auto flex items-center gap-1.5">
          {conversations.map((_, i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                i === activeConvo ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Chat Body */}
      <div className="px-5 py-5 min-h-[280px] flex flex-col justify-end">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeConvo}-${phase}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-3"
          >
            {/* User message */}
            <div className="flex justify-end">
              <div className="bg-blue-500 text-white text-sm px-4 py-2.5 rounded-2xl rounded-br-sm max-w-[80%] shadow-sm">
                {convo.question}
              </div>
            </div>

            {/* AI response */}
            {phase === 'typing' && (
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[10px] font-bold">AI</span>
                </div>
                <div className="bg-gray-100 dark:bg-slate-700 rounded-2xl rounded-tl-sm px-3 py-2">
                  <TypingIndicator />
                </div>
              </div>
            )}

            {phase === 'answer' && (
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[10px] font-bold">AI</span>
                </div>
                <div className="bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 text-sm px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%]">
                  <p>{convo.answer}</p>
                  {convo.visual}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Chat Input Mock */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700/50 rounded-full px-4 py-2.5 border border-gray-200 dark:border-slate-600">
          <span className="text-sm text-gray-400 dark:text-slate-500 flex-1">Ask about your ESG data...</span>
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// --- Capability Cards ---

const capabilityCards = [
  {
    icon: Zap,
    title: 'Instant Answers',
    description: 'Get answers to any ESG question in seconds',
    gradient: 'from-amber-400 to-orange-500',
  },
  {
    icon: BarChart3,
    title: 'Visual Reports',
    description: 'Auto-generated charts and visualizations',
    gradient: 'from-blue-500 to-cyan-400',
  },
  {
    icon: BookOpen,
    title: 'Expert Knowledge',
    description: 'Trained on 35+ ESG frameworks and standards',
    gradient: 'from-purple-500 to-violet-400',
  },
];

// --- Main Section ---

const AIAssistantDemo = () => {
  return (
    <SectionWrapper
      id="ai-assistant-demo"
      bgClass="bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-[#0a0f1a] dark:to-[#0f172a]"
      padding="py-10 md:py-12"
    >
      {/* Header */}
      <div className="text-center mb-14">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          className="text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-gray-900 dark:text-white tracking-tight mb-5"
        >
          See the <span className="bg-gradient-to-r from-blue-600 to-purple-400 bg-clip-text text-transparent">AI Assistant</span> in Action
        </motion.h2>
      </div>

      {/* Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
        {/* Left: Chat Demo */}
        <ChatWindow />

        {/* Right: Capability Cards */}
        <div className="space-y-4 lg:pt-4">
          {capabilityCards.map((card, index) => (
            <motion.div
              key={card.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={index + 2}
              whileHover={{ x: 8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md border border-gray-100 dark:border-slate-700 cursor-default flex items-start gap-4 group"
            >
              <div
                className={`w-11 h-11 rounded-lg bg-gradient-to-br ${card.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}
              >
                <card.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">{card.title}</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 leading-relaxed">{card.description}</p>
              </div>
              <svg
                className="w-5 h-5 text-gray-300 dark:text-slate-600 group-hover:text-blue-400 transition-colors ml-auto mt-1 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </motion.div>
          ))}

          {/* Extra trust signal */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={5}
            className="flex items-center gap-3 px-5 pt-4"
          >
            <div className="flex -space-x-2">
              {['bg-blue-400', 'bg-emerald-400', 'bg-purple-400', 'bg-amber-400'].map((bg, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-full ${bg} border-2 border-white dark:border-slate-800 flex items-center justify-center`}
                >
                  <span className="text-white text-[10px] font-bold">
                    {['JR', 'KL', 'MT', 'AS'][i]}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              <span className="font-semibold text-gray-700 dark:text-slate-300">2,400+</span> ESG queries answered daily
            </p>
          </motion.div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default AIAssistantDemo;
